/**
 * English CV and performance-review excerpts for the curated cast.
 *
 * Translated rather than machine-flipped: the review text is what the rationale
 * quotes back at the reader ("Per your last performance review: …"), so a
 * stilted rendering would undercut the exact moment the demo is built around.
 * Swiss specifics are kept (Fachausweis, KV apprenticeship, cantonal banks) —
 * flattening them into generic HR English would make the people less real.
 */

export const CAST_ARTEFACTS_EN: Record<string, { cv?: string; mag?: string }> = {
    "cast-berger": {
        cv: `Sandra Berger, born 1991, resident in Winterthur.

Education
2007–2010 Commercial apprenticeship in banking (KV), Zürcher Kantonalbank
2013 Federal Diploma of Higher Education in Banking
2018 CAS Financial Advisory, Kalaidos University of Applied Sciences

Experience
2010–2016 Officer, counter services and payments, Zürcher Kantonalbank
2016–2019 Retail Client Advisor, Bank Cler, Winterthur
since 2019 Retail Client Advisor, Acme Bank, Rayon 3 (80%)

Focus: pension and financing advice for retail clients in the segment up to
USD 500,000 in assets. Manages a portfolio of around 340 client relationships.
Contributed to the "digital advisory file" project (2023) as a tester from the
sales side.

Languages: German (native), English (B2), French (A2)`,
        mag: `Performance review 2026 — excerpt

Assessment: Sandra consistently meets expectations and exceeds them on client
retention. Feedback from the Rayon is very good and her conversion rate is above
the team average.

Development themes from the conversation:
Sandra raises herself that clients increasingly ask about sustainable investment
solutions, and that she still feels uncertain when capturing sustainability
preferences and when handling critical follow-up questions on ESG products. She
would like to build this up deliberately.

Second theme: for more complex pension cases (pension fund buy-ins, retirement
planning) she regularly brings in the financial planner today. She would like to
cover more of this herself.

Agreement: development focus 2026/27 on sustainability. Employment level remains
at 80%.`,
    },

    "cast-kuebler": {
        cv: `Thomas Kübler, born 1983, resident in Baden.

Education
1999–2002 Commercial apprenticeship in banking (KV)
2006 Federal Diploma of Higher Education in Banking
2011 Certified Credit Specialist, ABS

Experience
2002–2008 Credit officer, Raiffeisen
2008–2011 Credit Analyst, retail clients, Acme Bank
since 2011 Credit Analyst, corporate clients, Acme Bank, Credit Centre

Credit authority A since 2018. Assesses roughly 400 credit applications per year
in the SME segment. Subject-matter owner for construction cost accounting and
general-contractor financing. Trains new analysts in the Credit Centre.`,
        mag: `Performance review 2026 — excerpt

Assessment: Thomas is the technical anchor of the Credit Centre. The quality of
his applications is excellent; internal audit has raised no findings on his files
in the last three reviews.

Development themes from the conversation:
Thomas would like to complement the single-case view with a portfolio view —
which concentration risks the loan book carries, and how early warning
indicators are read across the portfolio. He sees this as a precondition for
contributing more substantially in the credit committee.

Note from the line manager: please stop assigning foundation-level courses — this
happened twice in 2025 and caused understandable frustration.

Agreement: focus on risk portfolio topics.`,
    },

    "cast-vogt": {
        cv: `Andrea Vogt, born 1987, resident in Zurich.

Education
2003–2006 Commercial apprenticeship in banking (KV)
2011 Federal Diploma of Higher Education in Banking
2019 CAS Leadership, HWZ Zurich

Experience
2006–2015 Client Advisor, various institutions
2015–2026 Retail Client Advisor, Acme Bank, Rayon 3
since 01.04.2026 Team Lead Rayon 3 (7 team members)

Long-standing advisor with very strong client retention. In her first leadership
role since April 2026.`,
        mag: `Performance review 2026 — excerpt

Andrea took over as Team Lead of Rayon 3 in April 2026. The transition has been
smooth on the technical side and the team accepts her.

Development themes from the conversation:
Andrea describes the change of role as the biggest adjustment. She found her
first feedback conversations difficult, particularly when giving critical
feedback to former peers. She tends to do tasks herself rather than delegate.

Agreement: prioritise leadership training in the first year. Andrea is aware that
the 2027 review round is coming up, which she will run herself for the first
time.`,
    },

    "cast-frei": {
        cv: `Nadine Frei, born 1989, resident in Uster.

Education
2005–2008 Commercial apprenticeship in banking (KV)
2014 Federal Diploma of Higher Education in Banking

Experience
2008–2015 Officer and advisor, Acme Bank
2015–2024 Retail Client Advisor, Acme Bank, Rayon 3 (100%)
2024–2026 Maternity leave and unpaid leave
since 03/2026 Returned as Retail Client Advisor (60%)`,
        mag: `Performance review 2026 — excerpt (return-to-work conversation)

Nadine returned in March 2026 after 22 months of absence, at an employment level
of 60%. She has settled back in quickly on the technical side and client feedback
is unchanged: very good.

Themes from the conversation:
During her absence the digital advisory journey was reworked and several
directives were revised. Nadine names this herself as her biggest gap. In
addition, several mandatory courses have expired.

Nadine points out that her time budget at 60% is limited and that she can only
schedule training in blocks.

Agreement: mandatory training first, then catching up on the digital topics. No
additional load in the first half-year.`,
    },

    "cast-raman": {
        cv: `Priya Raman, born 1995, resident in Zurich.

Education
2014–2018 BSc Computer Science, ZHAW
2021 Azure Solutions Architect Associate

Experience
2018–2021 Software Engineer, Swisscom
since 2021 Software Engineer, Acme Bank, IT Risk & BCM

Develops and operates internal risk reporting applications. Technical contact
for the BCM toolchain. Guest member of the architecture board since 2025.`,
        mag: `Performance review 2026 — excerpt

Assessment: Priya delivers consistently high technical quality and is the go-to
person in the team for the BCM toolchain.

Development themes from the conversation:
Priya would like to move towards project management in the medium term. She named
two themes herself: first, she lacks confidence in threat modelling and secrets
management during the code review process. Second, she would like to represent
results more confidently in front of the architecture board — today she mostly
leaves that to the team lead.

Agreement: the career goal of project management is supported. Priya stays
technically anchored in 2026/27 and builds method skills in parallel.`,
    },

    "cast-bianchi": {
        cv: `Luca Bianchi, born 2007, resident in Dietikon.

Education
since 08/2025 Commercial apprenticeship in banking (EFZ), Acme Bank

Rotations so far
08/2025–02/2026 Client service and payments
since 03/2026 Rayon 3, advisory support`,
        mag: `Progress review 2026 — excerpt

Luca is in his first apprenticeship year and moved into the advisory area in
March 2026. He works reliably and has been well received by the team.

Themes from the conversation:
Prioritisation and self-organisation are still difficult for him, particularly
when several tasks arrive at once. As expected at this stage, he still lacks
product and process knowledge.

Agreement: structured build-up over the remainder of the apprenticeship.`,
    },

    "cast-rossi": {
        mag: `Performance review 2026 — excerpt

Marco has been in advisory for three years and is developing well. He notes
himself that he hands almost all pension cases to financial planning today, and
would like to be able to cover this area himself.

Agreement: the development goal towards financial planning is supported.`,
    },
}
